import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { AccountDetails, AppPlan, Card, PlanLimit } from "models/interfaces";
import {
  AddOnInvoicePreview,
  BillingConfig,
  BillingSummary,
  PlanSwitchPreview,
  SeatChangeRequest,
  SeatReductionRequired,
  SubscriptionState,
  UserStatus,
} from "types/perSeat";
import {
  SmsCreditActivityEntry,
  SmsCreditBalance,
  SmsCreditPackList,
  SmsCreditPurchaseRequest,
  SmsCreditPurchaseResult,
  SmsCreditQuote,
} from "types/smsCredit";
import history from "utils/history";
import { IdvMethod } from "types/identityVerification";
import { getCallbackKeyFromUrl } from "helpers/methods";
import { getEmbedIdvMethods, isEmbedEditorMode } from "embed/EmbedEditorMode";
import {
  getAccessToken,
  getCallParams,
  getNoAuthCallParams,
  makeCall,
} from "utils/service";

export const getDocumentDimensions = async (
  requestFile: any,
  isRequest: boolean,
  envelopeId: string,
  isPublic: boolean,
  abortSignal?: AbortSignal | undefined
) => {
  const url = isPublic
    ? `${urls.FETCH_DIMENSIONS_CALLBACK}/${envelopeId}?id=${encodeURIComponent(envelopeId)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`
    : `${urls.FETCH_DIMENSIONS}/${isRequest}/envelopeId/${envelopeId}`;
  try {
    const callParams: any = (await isPublic)
      ? getNoAuthCallParams("POST", requestFile)
      : getCallParams("POST", requestFile);
    callParams.signal = abortSignal;
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchPlanUsage = async (
  setPlanLimits: (planLimits: Map<string, PlanLimit>) => void
) => {
  try {
    const [selfSigned, notariusVerified] = await Promise.all([
      fetchPlanLimit(strings.selfSigned),
      fetchPlanLimit(strings.notariusVerifiedEnvelope),
    ]);

    const planLimits = new Map<string, PlanLimit>();
    planLimits.set(strings.selfSigned, selfSigned);
    planLimits.set(strings.notariusVerifiedEnvelope, notariusVerified);
    setPlanLimits(planLimits);
  } catch (error: any) {
    throw error;
  }
};

export const fetchAccountPlanDetails = async (
  accountName: string,
  setPlanStatus: (planStatus: string) => void,
  setCurrentPlan: (currentPlan: AppPlan) => void,
  setCards: (cards: Card[]) => void,
  setAccountDetails: (accountDetails: AccountDetails) => void
) => {
  try {
    const [
      planStatus,
      currentPlan,
      // cards,
      accountDetails,
    ] = await Promise.all([
      fetchPlanStatus(),
      fetchCurrentPlan(accountName),
      // getCards(),
      fetchAccountDetails(),
    ]);

    setPlanStatus(planStatus);
    setCurrentPlan(currentPlan);
    // setCards(cards);
    setAccountDetails(accountDetails);
  } catch (error: any) {
    throw error;
  }
};

const fetchPlanLimit = async (entity: string) => {
  const url = `${urls.FETCH_PLAN_LIMIT}/${entity}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getCards = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.FETCH_CARDS, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deleteCard = async (id: String) => {
  try {
    const callParams = await getCallParams("DELETE");
    const response: any = await makeCall(
      `${urls.DELETE_CARD}/${id}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const addPaymentMethods = async (id: String, status: boolean) => {
  const url = `${urls.ADD_CARD}/${id}/${status}`;
  try {
    const callParams = await getCallParams("POST");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchPlanStatus = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.FETCH_PLAN_STATUS, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAccountDetails = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      urls.FETCH_ACCOUNT_DETAILS,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchCurrentPlan = async (accountName: string) => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      `${urls.FETCH_CURRENT_PLAN}/${accountName}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export interface AccessibleFeature {
  key: string;
  enabled: boolean;
  disabledMessage?: string;
}

export const fetchAccountAccess = async (): Promise<AccessibleFeature[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response: unknown = await makeCall(urls.ACCESS_FEATURES, callParams);
    return Array.isArray(response) ? (response as AccessibleFeature[]) : [];
  } catch (error: any) {
    throw error;
  }
};

export const activateUser = async (
  user: any,
  token: string,
  addDiscount: boolean
) => {
  try {
    const callParams = await getAccessToken("PATCH", user, token);
    const response: any = await makeCall(
      `${urls.ACTIVATE_NEW_USER}/discount/${addDiscount}`,
      callParams
    );
    return response;
  } catch (error: any) {
    history.push(viewpaths.landingViewPath);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Per-seat billing
// ---------------------------------------------------------------------------

/**
 * The catalog answers with its plans **grouped by billing cycle**
 * (`{ Monthly: [...], Annual: [...] }`), not as a flat array — every caller
 * here wants one list and re-derives the cycle from each plan's own
 * `validityInDays` (`groupPlansByCycle`), so the groups are flattened on the
 * way in, Monthly first. A flat array response is still passed through, and
 * anything else yields an empty catalog.
 */
const flattenPlansCatalog = (response: unknown): AppPlan[] => {
  if (Array.isArray(response)) return response as AppPlan[];
  if (!response || typeof response !== "object") return [];
  return Object.values(response as Record<string, unknown>).reduce(
    (plans: AppPlan[], group: unknown) =>
      Array.isArray(group) ? plans.concat(group as AppPlan[]) : plans,
    [],
  );
};

/**
 * Public plan catalog for the current app, including each plan's `metadata`
 * — the only source of the `perSeatBilling` flag. The admin-scoped catalog
 * endpoint this used to call always 403s for a normal account, so this uses
 * the public plans endpoint instead.
 */
export const fetchPlansCatalog = async (app: string): Promise<AppPlan[]> => {
  try {
    const callParams = await getNoAuthCallParams("GET");
    const response: unknown = await makeCall(
      `${urls.GET_ALL_PLANS}/${app}`,
      callParams
    );
    return flattenPlansCatalog(response);
  } catch (error: any) {
    throw error;
  }
};

/**
 * Cost of changing to `count` total seats. Preview only — nothing is
 * charged. Works for both an increase and a decrease from the current count.
 */
export const fetchSeatPreview = async (
  count: number,
): Promise<AddOnInvoicePreview> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      `${urls.SEAT_PREVIEW}/${count}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Set the purchased seat count. Buying seats charges the prorated difference
 * immediately; releasing them credits the unused part to the account balance.
 *
 * Dropping below the active-user count needs `keepUserIds` — who stays active,
 * at most `count` of them. Without it the call rejects with `409` and a
 * `SeatReductionRequired` body listing the candidates. `402` means the charge
 * for extra seats was declined; the count is unchanged.
 */
export const updateSeatCount = async (
  count: number,
  keepUserIds?: number[]
) => {
  try {
    const body: SeatChangeRequest | undefined = keepUserIds
      ? { keepUserIds }
      : undefined;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      `${urls.MANAGE_SEATS}/${count}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Whether switching to `planId` needs a seat reduction first. `users` comes back
 * only when one is required.
 */
export const fetchSeatCheck = async (
  planId: string,
  seats?: number
): Promise<SeatReductionRequired> => {
  try {
    const callParams = await getCallParams("GET");
    // `seats` tells the check how many the customer is buying, so `keepCount`
    // comes back as min(seats, plan cap) rather than the cap alone.
    const query = seats && seats > 0 ? `?seats=${seats}` : "";
    const response: any = await makeCall(
      `${urls.SEAT_CHECK}/${encodeURIComponent(planId)}/seat/check${query}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Exact cost of switching to `planId` — read-only, nothing is charged. Safe to
 * call on every checkout-page load and every seat-count change.
 *
 * The switch is anchor-reset: the new plan is charged in full for a fresh
 * cycle starting today, and `creditForUnusedTime` on the outgoing plan (if
 * any) is applied against that — see `PlanSwitchPreview`.
 *
 * `keepUserIds` only matters once the seat-reduction picker (`fetchSeatCheck`)
 * has been resolved; sending it before that is pointless. Without it, on a
 * downgrade that needs one, this rejects with the same `409`
 * `SeatReductionRequired` body the switch itself would.
 */
export const fetchPlanSwitchPreview = async (
  planId: string,
  seats?: number,
  keepUserIds?: number[]
): Promise<PlanSwitchPreview> => {
  try {
    const callParams = await getCallParams("GET");
    const params = new URLSearchParams();
    if (seats && seats > 0) params.set("seats", String(seats));
    if (keepUserIds?.length) params.set("keepUserIds", keepUserIds.join(","));
    const query = params.toString() ? `?${params.toString()}` : "";
    const response: any = await makeCall(
      `${urls.SEAT_CHECK}/${encodeURIComponent(planId)}/switch/preview${query}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Switch or downgrade the plan.
 *
 * `seats` is **mandatory on a per-seat plan** and must be sent on every switch.
 * The backend has no "carry the current count over" path: with the field absent
 * it falls back to `keepUserIds.size()`, and with no body at all to the
 * account's *active-user* count — billing the customer for the people they have
 * rather than the seats they bought, and overwriting `user.limit` with that
 * number. Only a flat-billed (non-per-seat) plan may send no body.
 *
 * `keepUserIds` is required when the account has more billable users than the
 * seats being bought — without it the call rejects with `409` and a
 * `SeatReductionRequired` body.
 */
export const switchPlan = async (
  planId: string,
  addDiscount: boolean,
  keepUserIds?: number[],
  seats?: number
) => {
  try {
    // Whole seats only — the quantity goes straight to Stripe.
    const quantity =
      seats && seats > 0 ? Math.max(1, Math.floor(seats)) : undefined;
    const body: SeatChangeRequest | undefined =
      keepUserIds || quantity
        ? {
            ...(keepUserIds ? { keepUserIds } : {}),
            ...(quantity ? { seats: quantity } : {}),
          }
        : undefined;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      `${urls.UPDATE_PLAN}/${encodeURIComponent(
        planId
      )}/discount/${addDiscount}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * App-level billing flags. The first of the two per-seat gates — the plan's own
 * `metadata.perSeatBilling` is the second, and both must be on.
 */
export const fetchBillingConfig = async (): Promise<BillingConfig> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.BILLING_CONFIG, callParams);
    // `Boolean("false")` is true, so the flags are matched against what "on"
    // actually looks like rather than coerced — this gate must fail closed.
    const isOn = (value: any) =>
      value === true ||
      value === 1 ||
      (typeof value === "string" &&
        ["true", "1", "yes"].includes(value.trim().toLowerCase()));
    return {
      perSeatBillingEnabled: isOn(response?.perSeatBillingEnabled),
      offerEnabled: isOn(response?.offerEnabled),
    };
  } catch (error: any) {
    throw error;
  }
};

/**
 * Billing overview: gross cost, the credit saved on the account and the net due
 * next renewal.
 *
 * Rejects when there is no active subscription (trial, cancelled) — but this
 * rethrows every failure alike, so callers cannot tell that apart from a 500 or
 * a dropped connection. Both land as "no credit line to show", which errs the
 * safe way: the credit is still spent by Stripe, so the worst case is quoting a
 * charge higher than the customer is actually billed. Splitting the two needs a
 * documented status for "no subscription" from the endpoint.
 */
export const fetchBillingSummary = async (): Promise<BillingSummary> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.BILLING_SUMMARY, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * ACTIVE / CANCELLED / NEVER_SUBSCRIBED — safe to call for any account, never
 * errors for one with no subscription. Gate the other billing calls on this
 * rather than calling them blind.
 */
export const fetchSubscriptionState = async (): Promise<SubscriptionState> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.SUBSCRIPTION_STATE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Current subscription (account + plan). */
export const fetchSubscription = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.FETCH_SUBSCRIPTION, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Activate or deactivate a user. Neither costs anything — the seats are already
 * paid for — but activating needs a free one, so it can reject with `406`.
 */
export const updateUserStatus = async (id: number, status: UserStatus) => {
  try {
    const callParams = await getCallParams("PATCH");
    const response: any = await makeCall(
      `${urls.UPDATE_USER_STATUS}/${id}/status/${status}`,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Permanently remove a user; also frees the seat. */
export const deleteUser = async (email: string) => {
  try {
    const callParams = await getCallParams("POST", { email });
    const response: any = await makeCall(urls.DELETE_USER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

// ---------------------------------------------------------------------------
// SMS credits
// ---------------------------------------------------------------------------

/**
 * Wallet balance plus what is on sale. One call powers the whole SMS credits
 * card, and `addOn` is absent when credits aren't sold in this account's
 * billing currency — hide the buy control rather than offer a refused purchase.
 *
 * Rejects with `403` on a plan without SMS at all; the caller hides the
 * feature entirely in that case.
 */
/** Identity verification methods a vendor runs on this environment (see `useIdvSecurityOptions`). */
export const fetchAvailableIdvMethods = async (): Promise<IdvMethod[]> => {
  // Embedded wizard: the editor bundle carries them; this endpoint needs a login the iframe has not got.
  if (isEmbedEditorMode()) {
    return getEmbedIdvMethods() as IdvMethod[];
  }
  const callParams = await getCallParams("GET");
  const response: any = await makeCall(urls.IDV_AVAILABLE_METHODS, callParams);
  return Array.isArray(response) ? response : [];
};

export const fetchSmsCreditBalance = async (): Promise<SmsCreditBalance> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.SMS_CREDIT, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * The pack catalogue on its own. Read live from the plan table on every call,
 * so a repriced pack takes effect immediately — which is why this is what a
 * `409` "the price changed" is recovered with, alongside a fresh quote.
 *
 * `packs: []` means nothing is on sale in this account's billing currency.
 */
export const fetchSmsCreditPacks = async (): Promise<SmsCreditPackList> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.SMS_CREDIT_PACKS, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Price a quantity before buying. Computed the same way the charge is, so the
 * figure shown and the figure billed cannot drift — multiply locally for
 * instant feedback while typing, but confirm with this before enabling Buy.
 */
export const fetchSmsCreditQuote = async (
  quantity: number,
): Promise<SmsCreditQuote> => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      `${urls.SMS_CREDIT_QUOTE}/${quantity}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Charge the saved card and add the credits.
 *
 * Takes a **quantity, never a pack id** — a pack click sends that pack's
 * `credits`, the custom box sends what was typed, and the backend resolves
 * which tier prices it. `expectedTotal` is the figure that was on screen,
 * taken from the quote: if the price moved in between, the purchase is
 * refused with `409` rather than charging something the customer never saw.
 *
 * `requestId` is Stripe's idempotency key: mint one UUID when the buy dialog
 * opens and reuse it for every retry of that purchase, so a double-click
 * returns the same invoice instead of charging twice. A new id charges again.
 *
 * `PAID` means done and `balance` is already the new one. `REQUIRES_ACTION`
 * means the bank wants the cardholder to approve — nothing is charged yet and
 * the credits only land once Stripe notifies the backend after approval, so
 * never add them optimistically and never re-POST with a fresh `requestId`.
 */
export const buySmsCredits = async (
  body: SmsCreditPurchaseRequest,
): Promise<SmsCreditPurchaseResult> => {
  try {
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(urls.SMS_CREDIT_BUY, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * The credits activity feed, newest first — credits bought and credits spent
 * in one list, with sends grouped (one line per document, per kind, per day).
 *
 * Paged like the invoice list: `page` counts from **1** and the server derives
 * the offset from it, so page 2 at limit 25 starts at row 25. `limit` is capped
 * at 100 server-side, and the backend over-reads both halves of the feed to
 * page correctly, so a large page costs more than it looks. Unknown `type`
 * values are returned as-is and must still be rendered.
 */
export const fetchSmsCreditActivity = async (
  page: number = 1,
  limit: number = 25,
): Promise<SmsCreditActivityEntry[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response: unknown = await makeCall(
      `${urls.SMS_CREDIT_ACTIVITY}/${page}/limit/${limit}`,
      callParams,
    );
    return Array.isArray(response)
      ? (response as SmsCreditActivityEntry[])
      : [];
  } catch (error: any) {
    throw error;
  }
};
